import { useState, type KeyboardEvent } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

function parseTokens(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)
}

type EmailChipInputProps = {
  id?: string
  label: string
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
}

export default function EmailChipInput({
  id,
  label,
  values,
  onChange,
  placeholder = 'Add email and press Enter',
  disabled = false,
}: EmailChipInputProps) {
  const [draft, setDraft] = useState('')

  const commitDraft = () => {
    const next = parseTokens(draft)
    if (next.length === 0) return
    const merged = [...values]
    for (const token of next) {
      if (!merged.includes(token)) merged.push(token)
    }
    onChange(merged)
    setDraft('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      if (draft.trim()) {
        event.preventDefault()
        commitDraft()
        return
      }
    }
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commitDraft()
    }
    if (event.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1))
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>
      <div
        className="mt-1 flex min-h-[42px] flex-wrap items-center gap-1.5 rounded border border-gray-300 px-2 py-1.5 focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-300"
      >
        {values.map((value) => (
          <span
            key={value}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 py-1 pl-2.5 pr-1 text-sm text-gray-800 ring-1 ring-gray-200/80"
          >
            <span className="max-w-[14rem] truncate">{value}</span>
            <button
              type="button"
              className="rounded-full p-0.5 text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-800"
              aria-label={`Remove ${value}`}
              disabled={disabled}
              onClick={() => onChange(values.filter((entry) => entry !== value))}
            >
              <XMarkIcon className="h-4 w-4" aria-hidden />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="email"
          className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50"
          value={draft}
          disabled={disabled}
          placeholder={values.length === 0 ? placeholder : undefined}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitDraft}
        />
      </div>
    </div>
  )
}
